import { cn } from "@heroui/react";
import { AnimatePresence, m } from "framer-motion";
import { resolveDemoIcon } from "../icons/defaultIcons";
import type { InboxDemoIcons, InboxGroup } from "../data/types";

type GroupItemProps = {
  group: InboxGroup;
  icons: InboxDemoIcons;
  depth?: number;
  selectedGroupId?: string;
  expandedGroupIds: string[];
  count?: number;
  onToggle: (groupId: string) => void;
  onSelect: (groupId: string) => void;
};

export default function GroupItem({
  group,
  icons,
  depth = 0,
  selectedGroupId,
  expandedGroupIds,
  count = 0,
  onToggle,
  onSelect,
}: GroupItemProps) {
  const hasChildren = Boolean(group.children?.length);
  const expanded = expandedGroupIds.indexOf(group.id) !== -1;
  const selected = selectedGroupId === group.id;
  const isSection = !group.iconKey && hasChildren && depth === 0;
  const selectable = !isSection && !hasChildren;
  const icon = resolveDemoIcon(icons, group.iconKey);
  const showBadge = Boolean(group.showCount && count > 0);

  const handleRowClick = () => {
    if (hasChildren) {
      onToggle(group.id);
      return;
    }
    if (selectable) onSelect(group.id);
  };

  const content = (
    <>
      {icon ? (
        <span className="flex size-4 shrink-0 items-center justify-center overflow-hidden">
          {icon}
        </span>
      ) : null}
      <span
        className={cn(
          "min-w-0 flex-1 truncate",
          isSection
            ? "flex-none text-xs text-default-500"
            : "text-sm text-default-foreground",
        )}
      >
        {group.label}
      </span>
      {showBadge ? (
        <span className="inbox-demo-count-badge">
          <span className="inbox-demo-count-badge__dot" />
          <span className="inbox-demo-count-badge__value">{count}</span>
        </span>
      ) : null}
      {hasChildren ? (
        <span
          className={cn(
            "flex h-3 w-3 shrink-0 items-center justify-center text-default-400 transition-transform duration-200",
            expanded ? "rotate-0" : "-rotate-90",
          )}
        >
          {resolveDemoIcon(icons, "chevronDown")}
        </span>
      ) : null}
    </>
  );

  return (
    <div className={cn("flex w-full flex-col items-start", isSection && "mt-3")}>
      <button
        aria-current={selected ? "true" : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors duration-150",
          isSection ? "py-1" : "p-2",
          selected && "bg-[var(--inbox-demo-selected)]",
          "hover:bg-default-100",
        )}
        type="button"
        onClick={handleRowClick}
      >
        {content}
      </button>
      <AnimatePresence initial={false}>
        {hasChildren && expanded ? (
          <m.div
            animate={{ height: "auto", opacity: 1 }}
            className="w-full overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className={cn(
                "flex w-full",
                depth >= 0 && group.iconKey ? "items-stretch pl-4" : "flex-col",
              )}
            >
              {group.iconKey ? (
                <>
                  <span aria-hidden className="w-px shrink-0 bg-default-200" />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    {group.children?.map((child) => (
                      <GroupItem
                        count={0}
                        depth={depth + 1}
                        expandedGroupIds={expandedGroupIds}
                        group={child}
                        icons={icons}
                        key={child.id}
                        selectedGroupId={selectedGroupId}
                        onSelect={onSelect}
                        onToggle={onToggle}
                      />
                    ))}
                  </div>
                </>
              ) : (
                group.children?.map((child) => (
                  <GroupItem
                    count={0}
                    depth={depth + 1}
                    expandedGroupIds={expandedGroupIds}
                    group={child}
                    icons={icons}
                    key={child.id}
                    selectedGroupId={selectedGroupId}
                    onSelect={onSelect}
                    onToggle={onToggle}
                  />
                ))
              )}
            </div>
          </m.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
